import callSkypeApi from './skypeApiUtils/callSkypeApi';
import checkForTeamsFallback from './skypeApiUtils/checkForTeamsFallback';
import loadSkypeConfig from './skypeApiUtils/loadSkypeConfig';
import runWithOauthIframe from './skypeApiUtils/runWithOauthIframe';
import type { ShellClientData } from '@suiteux/suiteux-shell';
import { PerformanceDatapoint } from 'owa-analytics';
import { getVariantEnvironment, isDogfoodEnv } from 'owa-metatags';
import { isFeatureEnabled } from 'owa-feature-flags';
import { getCurrentCulture } from 'owa-localize';
import makeSkypeApiCallViaFetch, {
    buildPolicyRequestUrl,
} from './skypeApiUtils/makeSkypeApiCallViaFetch';

interface SkypeLink {
    href: string;
}

interface SkypeApiResponse {
    _links: {
        self?: SkypeLink;
        user?: SkypeLink;
        applications?: SkypeLink;
        policies?: SkypeLink;
        xframe?: SkypeLink;
        redirect?: SkypeLink;
    };
    teamsOnlyHeaderSet?: boolean;
}

type SkypeHrefKey = keyof SkypeApiResponse['_links'];

interface SkypePolicies {
    redirectClient: string;
}

const ENDPOINT_ID = 'd97583cc-9781-4a59-9628-06c4e609c65b';
const MAX_REDIRECT_COUNT = 10;
const DATAPOINT_NAME = 'SkypeCheckForTeams';

/**
 * Check if current user should use Teams or Skype
 * This is done with 4 steps:
 *
 * Step 1: Load script configuration
 * The result will contain configuration like below which is used for implicit OAuth:
 * {
 *     "SkypeLyncWebExperience": {
 *         "implicitOAuthParams": {
 *             "client_id":"e48d4214-364e-4731-b2b6-47dabf529218",
 *             "origins": [
 *                 {
 *                     "origin":"https://webdir0m.online.lync.com/Autodiscover/AutodiscoverService.svc/root",
 *                     "xframe":"https://webdir0m.online.lync.com/xframe"
 *                 }
 *                 ...
 *
 * Step 2: Call root to get user urls like below:
 * {
 *    "_links":{
 *        "user": {
 *            "href": "https://webpooldm20m05.infra.lync.com/ucwa/oauth/user",
 *        },
 *        "xframe":{
 *            "href": "https://webpooldm20m05.infra.lync.com/Autodiscover/XFrame/XFrame.html"
 *        }
 *    }
 * }
 *
 * Step 3: Call user to get user api urls like below:
 * {
 *    "_links":{
 *        "applications": {
 *            "href": "https://webpooldm20m05.infra.lync.com/ucwa/oauth/v1/applications",
 *            "revision": "2"
 *        },
 *        "xframe":{
 *            "href": "https://webpooldm20m05.infra.lync.com/Autodiscover/XFrame/XFrame.html"
 *        }
 *    }
 * }
 *
 * This step 2 & 3 may contains "redirect" result so we need to a loop for redirection
 *
 * A redirection response looks like this:
 *
 * {
 *    "_links":{
 *        "redirect": {
 *            "href": "https://webdir0m.online.lync.com/Autodiscover/AutodiscoverService.svc/root",
 *        },
 *        "xframe":{
 *            "href": "https://webdir0m.online.lync.com/xframe"
 *        }
 *    }
 * }
 *
 * Step 4: Call applications to get Application urls like below:
 * {
 *     "_links": {
 *         "policies": {
 *             "href": "/ucwa/oauth/v1/applications/112831675289/policies"
 *         }
 *     }
 * }
 *
 * Step 5: Call policies to get user policies and whether should use teams:
 * {
 *     "redirectClient": "Enabled"
 * }
 */
export default async function checkForTeams(shellClientData: ShellClientData): Promise<boolean> {
    let useFetch = false;

    if (isFeatureEnabled('fwk-skypeBusinessV2CheckNoIframe')) {
        useFetch = true;
    }

    // Non Prod / Dogfood users should not use the old method.
    // Therefore, if they're not enabled for using the experimental fetch API
    // We should bail out here.
    if (!(getVariantEnvironment() === 'Prod' || isDogfoodEnv() || useFetch)) {
        return false;
    }

    if (isFeatureEnabled('fwk-skypeBusinessV2CheckWithFallback')) {
        return checkForTeamsFallback(shellClientData);
    }

    // Step 1: Load script configuration
    const skypeConfig = await loadSkypeConfig(shellClientData.UPN, shellClientData.UID);
    const oauthParams = skypeConfig?.SkypeLyncWebExperience?.implicitOAuthParams;

    const clientId = oauthParams?.client_id;
    const origins = oauthParams?.origins;
    const count = origins ? origins.length : 0;

    for (let i = 0; i < count; i++) {
        const dataPoint = new PerformanceDatapoint(DATAPOINT_NAME);

        const triedUris: string[] = [];
        let redirectCount = 0;
        let root = origins[i].origin;
        let xframe = origins[i].xframe;
        let applications: string;
        let user: string;
        let response: SkypeApiResponse;

        while (
            !applications &&
            root &&
            triedUris.indexOf(root) < 0 &&
            redirectCount < MAX_REDIRECT_COUNT
        ) {
            if (useFetch) {
                response = await makeSkypeApiCallViaFetch<SkypeApiResponse>('GET', xframe, root);
            } else {
                // Step 2: Call 'root' to get user url
                response = await runWithOauthIframe(
                    xframe,
                    null /*redirect*/,
                    null /*clientId*/,
                    contentWindow => callSkypeApi<SkypeApiResponse>('GET', root, contentWindow)
                );
            }

            // Step 3: Call 'user' to get user applications urls
            user = safeGetHref(response, 'user');
            xframe = safeGetHref(response, 'xframe');

            if (useFetch) {
                response = await makeSkypeApiCallViaFetch<SkypeApiResponse>('GET', user, user);
            } else {
                response = await runWithOauthIframe(user, xframe, clientId, contentWindow =>
                    callSkypeApi<SkypeApiResponse>('GET', user, contentWindow)
                );
            }

            // Due to Teams users lacking pools, this step will throw an error for teams users when they're migrated.
            // However, we can check for the presence of the 'X-MS-TeamsOnly' header to know if we can safely short circuit
            // Only Teams users will have this header set to true.
            if (response?.teamsOnlyHeaderSet) {
                return true;
            }

            applications = safeGetHref(response, 'applications');

            redirectCount++;
            triedUris.push(root);
            root = safeGetHref(response, 'redirect');
            xframe = safeGetHref(response, 'xframe');
        }

        let policies: string;
        let policy: SkypePolicies;

        // Step 4: Call 'applications' to get policies url
        if (applications) {
            const postData = getApplicationsPostData();
            xframe = safeGetHref(response, 'xframe');
            if (useFetch) {
                response = await makeSkypeApiCallViaFetch<SkypeApiResponse>(
                    'POST',
                    applications,
                    applications,
                    postData
                );
                policies = safeGetHref(response, 'policies');

                if (policies) {
                    const policiesUrl = buildPolicyRequestUrl(xframe, policies);
                    policy = await makeSkypeApiCallViaFetch<SkypePolicies>(
                        'GET',
                        applications,
                        policiesUrl
                    );
                }
            } else {
                policy = await runWithOauthIframe(
                    applications,
                    xframe,
                    clientId,
                    async contentWindow => {
                        response = await callSkypeApi<SkypeApiResponse>(
                            'POST',
                            applications,
                            contentWindow,
                            postData
                        );

                        // Step 5: Call 'policies' to get redirection policy
                        policies = safeGetHref(response, 'policies');

                        return (
                            policies && callSkypeApi<SkypePolicies>('GET', policies, contentWindow)
                        );
                    }
                );
            }
        }

        dataPoint.addCustomData({
            index: i,
            root,
            user,
            applications,
            policies,
            enabled: policy ? policy.redirectClient : 'NULL',
            xframe,
            redirectCount,
            triedUris: triedUris.join('|'),
        });
        dataPoint.end();

        if (policy) {
            return policy.redirectClient == 'Enabled';
        }
    }

    return false;
}

function getApplicationsPostData() {
    return {
        Culture: getCurrentCulture(),
        EndpointId: ENDPOINT_ID,
        UserAgent: 'Exchange',
    };
}

function safeGetHref(response: SkypeApiResponse, name: SkypeHrefKey): string {
    const links = response?._links;
    const href = links?.[name];
    return href ? href.href : null;
}
